import React, { useEffect, useState } from 'react';
import { Spin, Input, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import httpClient from '../../utils/HttpClient';
import { SUBSCRIPTIONS_HISTORY_URL } from '../../constants/Url';
import { useNavigate } from 'react-router-dom';
import { IoIosArrowBack, IoIosSearch } from "react-icons/io";
import { FiCopy } from "react-icons/fi";
import { RiFileListLine } from "react-icons/ri";
import { AlipayCircleFilled, WechatFilled } from '@ant-design/icons';
import { BsCheckCircleFill } from 'react-icons/bs';
import CustomPagination from '../../components/common/CustomPagination';
import dayjs from 'dayjs';

const PurchaseHistoryPage = ({ isHide, refreshTrigger }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [total, setTotal] = useState(0);

  // Search/Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchHistory(pageNo, pageSize);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [pageNo, pageSize, searchQuery, filterType, refreshTrigger]);

  const fetchHistory = async (page, limit) => {
    try {
      setLoading(true);
      const res = await httpClient.get(SUBSCRIPTIONS_HISTORY_URL, {
        params: {
          pageNo: page,
          pageSize: limit,
          keyword: searchQuery || undefined,
          paymentType: filterType === 'All' ? undefined : filterType,
        },
      });
      if (res?.data?.status === 200 || res?.data?.status === 201) {
        setHistory(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch purchase history', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('text_copied'));
  };

  const mapOrderType = (orderType) => {
    switch (orderType?.toLowerCase()) {
      case 'pro': return 'PRO';
      case 'vip': return 'VIP';
      case 'normal': return 'Normal';
      default: return (orderType || '-').toUpperCase();
    }
  };

  const mapOrderTypes = (orderType) => {
    switch (orderType?.toLowerCase()) {
      case 'pro': return t("text_member");
      case 'vip': return t("text_svip_member");
      // case 'normal': return t("text_normal");
      default: return ''// (orderType || '-').toUpperCase();
    }
  };

  const mapMethod = (method) => {
    switch(method?.toLowerCase()) {
      case 'wechatpay': return 'WeChat Pay';
      case 'alipay': return 'Alipay';
      default: return method || '-';
    }
  };
  
  const mapType = (type) => {
    switch(type?.toLowerCase()) {
      case 'activate': return 'Activate';
      case 'renew': return 'Renew';
      case 'upgrade': return 'Upgrade';
      default: return type ? type.charAt(0).toUpperCase() + type.slice(1) : '-';
    }
  };


  const filteredHistory = history;
  const renderPaymentMethod = (method) => {
    switch (method) {
      case 'alipay':
        return <AlipayCircleFilled className="text-[#1677ff] text-[22px]" />;

      case 'wechatpay':
        return <WechatFilled className="text-[#09b83e] text-[22px]" />;

      default:
        return (
          <span className="text-capitalize">
            {t(method || '-')}
          </span>
        );
    }
  };

  const totalAmount = filteredHistory.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <div className={`w-full flex flex-col font-sans ${isHide ? '' : 'min-h-screen'}`}>
      {!isHide && (
        <>
          {/* Desktop Header */}
          <div className="hidden lg:flex esi-std-container w-full mx-auto pt-10 pb-6 items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-2 font-medium"
            >
              <IoIosArrowBack className="text-xl" />
              <span>{t('text_back')}</span>
            </button>
            <span className="text-gray-300">|</span>
            <h1 className="text-[22px] font-bold text-gray-900 tracking-wide">{t('text_purchase_history')}</h1>
          </div>

          {/* Mobile Header */}
          <div className="lg:hidden pt-4 pb-3 px-4">
            <div className="flex items-center justify-center relative">
              <button
                onClick={() => navigate(-1)}
                className="absolute left-0 text-[24px] text-gray-800"
              >
                <IoIosArrowBack />
              </button>
              <h1 className="text-[17px] font-semibold text-gray-900 tracking-wide">{t('text_purchase_history')}</h1>
            </div>
          </div>
        </>
      )}

      <div className={`w-full mx-auto flex-1 flex flex-col pb-12 ${isHide ? '' : 'esi-std-container'}`}>
        <Spin spinning={loading}>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-4">
            {/* Table Header Controls */}
            <div className="p-4 lg:p-5 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 border-b border-gray-100">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">{t("text_purchase_history")}</span>
                    </div>
                    {/* <span className="text-sm text-gray-500">
                        ({filteredHistory.length} 显示 · ¥{totalAmount.toFixed(2)})
                    </span> */}
                </div>
                
                <div className="flex items-center gap-3 w-full lg:w-auto">
                    <div className="relative w-full lg:w-[280px]">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <IoIosSearch className="text-gray-400 text-lg" />
                        </div>
                        <input
                            type="text"
                            placeholder=""
                            className="w-full pl-9 pr-4 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <Select
                        defaultValue="All"
                        value={filterType}
                        onChange={setFilterType}
                        className="w-[100px]"
                        options={[
                            { value: 'All', label: t('text_all') },
                            { value: 'activate', label: t('text_activate') },
                            { value: 'renewal', label: t('text_renew') },
                            { value: 'upgrade', label: t('text_upgrade') }
                        ]}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                        <tr className="bg-[#fcfdfd] border-b border-gray-100 text-sm font-semibold text-gray-500">
                            {/* <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">会员ID</th>
                            <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">手机号</th> */}
                            <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">{t("text_order_num")}</th>
                            <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">{t("text_paid_amount")}</th>
                            <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">{t("text_paid_time")}</th>
                            <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">{t("text_payment_type")}</th>
                            <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">{t("text_plan")}</th>
                            <th className="py-3 px-5 font-medium whitespace-nowrap text-[#333333]">{t("text_payment_method")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredHistory.length > 0 ? (
                            filteredHistory.map((item, idx) => (
                                <tr key={item._id || idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors text-[14px]">
                                    {/* <td className="py-4 px-5 text-gray-500 whitespace-nowrap">
                                        {item?.memberId || ""}
                                    </td>
                                    <td className="py-4 px-5 text-gray-500 whitespace-nowrap">
                                        {item?.member?.phone ? `+${item.member.phone.countryCode} ${item.member.phone.number}` : ''}
                                    </td> */}
                                    <td className="py-4 px-5 text-gray-500">
                                        {item && item?.paymentId || ""}
                                    </td>
                                    <td className="py-4 px-5 text-gray-500">
                                        {`￥${item && item?.amount || ""}`}
                                    </td>
                                    <td className="py-4 px-5 text-gray-500">
                                        {item.time ? dayjs(item?.time).format("YYYY-MM-DD HH:mm") : ""}
                                    </td>
                                    <td className="py-4 px-5 text-gray-500 capitalize">
                                        {t(item && item.type || "")}
                                    </td>
                                    <td className="py-4 px-5 text-gray-500 capitalize text-nowrap">
                                        {t(mapOrderTypes(item?.orderType) || "")}
                                    </td>
                                    <td className="py-4 px-5 text-gray-500">
                                        {renderPaymentMethod(item?.method || "")}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="py-16 text-center text-gray-400">
                                    {t('text_no_purchase_record')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
          </div>
          
          {pageSize && (
            <div className="mt-8 flex justify-end pb-4">
              <CustomPagination
                current={pageNo}
                pageSize={pageSize}
                total={total}
                onChange={(page, limit) => {
                  setPageNo(page);
                  if (limit) setPageSize(limit);
                }}
              />
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default PurchaseHistoryPage;
